import React, { useState, useEffect, useRef } from "react";
import { createPriorityDMAPI, createServiceMeetingAPI, updatePriorityDMAPI, updateServiceMeetingAPI, createEvent, updateEvent, getCommunityByIdAPI, getUserByEmail, bookServiceMeetingAPI, bookServiceMeetingByBodyAPI, createWebinar, editWebinar, sendCommunityUpdate } from "../../../../Service/user";
import "./AddServicePopup.scss";
import { toast } from "react-hot-toast";
import { IoClose } from "react-icons/io5";
import Calendar from "react-calendar";
import { format } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addMinutes } from 'date-fns';

const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", options);
};


const AddServicePopup = ({ communityId, onClose, onServiceAdded, editingService }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [serviceType, setServiceType] = useState("priorityDM");
  const [selectedDays, setSelectedDays] = useState([]);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [showMemberSelect, setShowMemberSelect] = useState(false);
  const [memberSelectType, setMemberSelectType] = useState('');
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    amount: 0,
    timeline: {
      value: 1,
      unit: "hours"
    },
    duration: 30,
    topics: "",
    availability: [],
    eventType: "Private",
    discount: 0,
    memberEmail: "",
    startTime: "",
    webinarType: "Private",
    date: new Date(),
    endTime: addMinutes(new Date(), 30),
    image: null,
  });
  const editorRef = useRef();
  const [previewImage, setPreviewImage] = useState(null);
  const [communityMembers, setCommunityMembers] = useState([]);
  const [activeSlot, setActiveSlot] = useState(null);
  const [emailInput, setEmailInput] = useState('');

  // Initialize states based on editingService
  useEffect(() => {
    if (editingService) {
      // Set service type
      const type = editingService.timeline ? 'priorityDM' 
        : editingService.eventType ? 'event'
        : editingService.webinarType ? 'webinar'
        : 'meeting';
      setServiceType(type);

      // Set form data
      setFormData({
        title: editingService.title || "",
        description: editingService.description || "",
        amount: editingService.price || 0,
        timeline: editingService.timeline ? {
          value: Math.floor(editingService.timeline / 60),
          unit: "hours"
        } : {
          value: 1,
          unit: "hours"
        },
        duration: editingService.duration || 30,
        topics: editingService.topics?.join(", ") || "",
        availability: editingService.availability || [],
        eventType: "Private",
        discount: editingService.discount || 0,
        memberEmail: editingService.memberEmail || "",
        startTime: editingService.startTime || "",
        webinarType: editingService.webinarType || "Private",
        date: editingService.startTime ? new Date(editingService.startTime) : new Date(),
        endTime: editingService.endTime ? new Date(editingService.endTime) : addMinutes(new Date(), 30),
        image: editingService.image || null,
      });

      // Set preview image
      setPreviewImage(editingService.image || null);

      // Handle meeting availability
      if (editingService.availability) {
        const dates = editingService.availability.map(day => new Date(day.day));
        setSelectedDays(dates);
        
        if (type === 'meeting') {
          setShowTimeSlots(true);
        }
      }
    }
  }, [editingService]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  // Add function to fetch community members
  const fetchCommunityMembers = async () => {
    try {
      const response = await getCommunityByIdAPI(communityId);
      const members = response.data.members.map(member => ({
        email: member.member.email,
        name: `${member.member.firstName} ${member.member.lastName}`,
        id: member.member._id
      }));
      setCommunityMembers(members);
    } catch (error) {
      console.error("Error fetching community members:", error);
      toast.error("Failed to fetch community members");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let response;
      const serviceData = {
        ...formData,
        topics: formData.topics.split(",").map(topic => topic.trim()),
        timeline: serviceType === "priorityDM" 
          ? formData.timeline.unit === "hours" 
            ? formData.timeline.value * 60 
            : formData.timeline.value * 24 * 60
          : undefined,
        price: serviceType === "event" ? formData.amount : undefined,
        communityId: communityId
      };

      if (editingService) {
        switch(serviceType) {
          case "priorityDM":
            response = await updatePriorityDMAPI(communityId, editingService._id, serviceData);
            break;
          case "meeting":
            response = await updateServiceMeetingAPI(communityId, editingService._id, serviceData);
            break;
          case "event":
            response = await updateEvent(editingService._id, serviceData);
            break;
          case "webinar":
            response = await editWebinar(editingService._id, {
              ...serviceData,
              startTime: formData.startTime,
              duration: Number(formData.duration),
              price: Number(formData.amount),
              discount: Number(formData.discount || 0),
              webinarType: "Private",
              community: communityId
            });
        }
        
        // Send community update for service updates
        await sendCommunityUpdate({
          communityId: communityId,
          updateType: serviceType === "priorityDM" ? "PriorityDM" : 
                      serviceType === "meeting" ? "Meeting" : 
                      serviceType === "event" ? "Event" : "Webinar",
          description: `A ${serviceType} service "${formData.title}" has been updated in the community.`
        });
      } else {
        switch(serviceType) {
          case "priorityDM":
            response = await createPriorityDMAPI(communityId, serviceData);
            // Send community update
            await sendCommunityUpdate({
              communityId: communityId,
              updateType: "PriorityDM",
              description: `A new priority DM service "${formData.title}" has been added to the community.`
            });
            break;
          case "meeting":
            // Create the service
            response = await createServiceMeetingAPI(communityId, serviceData);
            
            // Book meetings for validated members sequentially instead of parallel
            for (const day of formData.availability) {
              const validSlots = day.slots.filter(slot => slot.memberEmail);
              
              for (const slot of validSlots) {
                try {
                  const user = await getUserByEmail(slot.memberEmail);
                  
                  const bookingData = {
                    slot: {
                      day: new Date(day.day).toISOString(),
                      startTime: slot.startTime,
                      endTime: slot.endTime
                    }
                  };

                  await bookServiceMeetingByBodyAPI(
                    communityId,
                    response.data._id,
                    user._id,
                    bookingData
                  );
                } catch (bookingError) {
                  console.error("Error booking slot:", bookingError);
                  // Continue with other bookings even if one fails
                  toast.error(`Failed to book slot for ${slot.memberEmail}`);
                }
              }
            }
            
            // Send community update
            await sendCommunityUpdate({
              communityId: communityId,
              updateType: "Meeting",
              description: `A new meeting service "${formData.title}" has been added to the community.`
            });
            break;
          case "event":
            response = await createEvent(serviceData);
            
            // Send community update
            await sendCommunityUpdate({
              communityId: communityId,
              updateType: "Event",
              description: `A new event "${formData.title}" has been scheduled in the community.`
            });
            break;
          case "webinar":
            const webinarData = {
              title: formData.title,
              description: formData.description,
              date: formData.date.toISOString(),
              startTime: formData.startTime.toISOString(),
              endTime: formData.endTime.toISOString(),
              duration: Number(formData.duration),
              price: Number(formData.amount),
              discount: Number(formData.discount || 0),
              webinarType: "Private",
              community: communityId,
              image: formData.image
            };

            if (editingService) {
              response = await editWebinar(editingService._id, webinarData);
            } else {
              response = await createWebinar(webinarData);
              // Send community update
              await sendCommunityUpdate({
                communityId: communityId,
                updateType: "Webinar",
                description: `A new webinar "${formData.title}" has been scheduled in the community for ${formatDate(formData.date)}.`
              });
            }
            break;
        }
      }

      toast.success(editingService ? "Service updated successfully!" : "Service created successfully!");
      onServiceAdded(response.data);
      onClose();
    } catch (error) {
      console.error("Error creating/updating service:", error);
      toast.error(error.message || error.response?.data?.message || "Failed to save service");
    } finally {
      setIsLoading(false);
    }
  };

  const generateTimeSlots = (startTime, endTime, duration) => {
    const slots = [];
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    
    while (start < end) {
      const slotEnd = new Date(start.getTime() + duration * 60000);
      if (slotEnd > end) break;
      
      slots.push({
        startTime: start.toTimeString().slice(0, 5),
        endTime: slotEnd.toTimeString().slice(0, 5)
      });
      
      start.setTime(slotEnd.getTime());
    }
    return slots;
  };

  const handleDaySelect = (date) => {
    // Create a new date object and set it to midnight in the local timezone
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    
    setSelectedDays(prev => {
      const exists = prev.some(d => {
        const prevDate = new Date(d);
        prevDate.setHours(0, 0, 0, 0);
        return prevDate.getTime() === localDate.getTime();
      });
      
      if (exists) {
        return prev.filter(d => {
          const prevDate = new Date(d);
          prevDate.setHours(0, 0, 0, 0);
          return prevDate.getTime() !== localDate.getTime();
        });
      }
      return [...prev, localDate];
    });
  };

  const handleTimeSlotSubmit = () => {
    const defaultStartTime = "09:00";
    // Calculate end time based on duration
    const startDate = new Date(`2000-01-01T${defaultStartTime}`);
    const endDate = new Date(startDate.getTime() + formData.duration * 60000);
    const defaultEndTime = endDate.toTimeString().slice(0, 5);
    
    const newAvailability = selectedDays.map(day => ({
      day: new Date(new Date(day).setHours(12, 0, 0, 0)),
      slots: [{
        startTime: defaultStartTime,
        endTime: defaultEndTime
      }]
    }));

    setFormData(prev => ({
      ...prev,
      availability: newAvailability
    }));
    setShowTimeSlots(true);
  };

  const handleSlotChange = (dayIndex, slotIndex, field, value) => {
    setFormData(prev => {
      const newAvailability = [...prev.availability];
      // Ensure the time change is valid
      const slot = newAvailability[dayIndex].slots[slotIndex];
      const startTime = field === 'startTime' ? value : slot.startTime;
      const endTime = field === 'endTime' ? value : slot.endTime;
      
      // Convert times to minutes for comparison
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      
      // Only update if end time is after start time
      if (endMinutes > startMinutes) {
        newAvailability[dayIndex].slots[slotIndex] = {
          ...slot,
          [field]: value,
          memberEmail: slot.memberEmail || ''
        };
      } else {
        toast.error("End time must be after start time");
      }
      
      return { ...prev, availability: newAvailability };
    });
  };

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Modify handleMemberSelect to include email validation
  const handleMemberSelect = async (email, dayIndex, slotIndex, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      // Validate email before setting
      const response = await getUserByEmail(email);
      if (!response?._id) {
        toast.error("This email is not registered");
        return;
      }

      setFormData(prev => {
        const newAvailability = [...prev.availability];
        newAvailability[dayIndex].slots[slotIndex] = {
          ...newAvailability[dayIndex].slots[slotIndex],
          memberEmail: email
        };
        return { ...prev, availability: newAvailability };
      });
      
      setShowMemberSelect(false);
      setActiveSlot(null);
      setMemberSelectType('');
      setEmailInput('');
      toast.success("Member added successfully");
    } catch (error) {
      console.error("Error validating email:", error);
      toast.error("Failed to validate email");
    }
  };

  // Add function to handle slot member selection
  const handleSlotMemberSelection = (dayIndex, slotIndex) => {
    setActiveSlot({ dayIndex, slotIndex });
    setShowMemberSelect(true);
  };

  // Add member selection handlers
  const handleMemberTypeSelect = (type) => {
    setMemberSelectType(type);
    if (type === 'community') {
      fetchCommunityMembers();
    }
    setShowMemberSelect(true);
  };

  // Add helper functions for date/time handling
  const formatDateTime = (date, time) => {
    const combinedDate = new Date(date);
    const timeDate = new Date(time);
    combinedDate.setHours(timeDate.getHours());
    combinedDate.setMinutes(timeDate.getMinutes());
    combinedDate.setSeconds(0);
    combinedDate.setMilliseconds(0);
    return combinedDate;
  };

  const handleDateChange = (date) => {
    if (!date) return;
  
    setFormData(prev => {
      const newDate = new Date(date);
  
      let currentStartTime;
      try {
        currentStartTime = new Date(prev.startTime);
        if (isNaN(currentStartTime.getTime())) {
          throw new Error("Invalid start time");
        }
      } catch {
        currentStartTime = new Date(); // fallback if invalid
      }
  
      newDate.setHours(currentStartTime.getHours());
      newDate.setMinutes(currentStartTime.getMinutes());
  
      return {
        ...prev,
        date: newDate,
        startTime: newDate,
        endTime: addMinutes(newDate, parseInt(prev.duration))
      };
    });
  };  

  const handleTimeChange = (time) => {
    if (!time) return;
    setFormData(prev => {
      const newStartTime = new Date(time);
      const newEndTime = addMinutes(newStartTime, parseInt(prev.duration));
      return {
        ...prev,
        startTime: newStartTime,
        endTime: newEndTime
      };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: reader.result
        }));
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="add-service-popup">
      <div className="popup-content">
        <div className="popup-header">
          <h2>{editingService ? "Edit Service" : "Add New Service"}</h2>
          <button className="close-button" onClick={onClose}>
            <IoClose size={24} />
          </button>
        </div>
        
        <div className="service-type-selector">
          <button
            type="button"
            className={`type-btn ${serviceType === "priorityDM" ? "active" : ""}`}
            onClick={() => setServiceType("priorityDM")}
          >
            Priority DM
          </button>
          <button
            type="button"
            className={`type-btn ${serviceType === "meeting" ? "active" : ""}`}
            onClick={() => setServiceType("meeting")}
          >
            Meeting
          </button>
          <button
            type="button"
            className={`type-btn ${serviceType === "event" ? "active" : ""}`}
            onClick={() => setServiceType("event")}
          >
            Event
          </button>
          <button
            type="button"
            className={`type-btn ${serviceType === "webinar" ? "active" : ""}`}
            onClick={() => setServiceType("webinar")}
          >
            Webinar
          </button>
        </div>

        {showMemberSelect && activeSlot && (
          <div className="member-selection-modal">
            <div className="member-selection">
              <h4>Select Member</h4>
              <div className="member-select-buttons">
                <button
                  type="button"
                  className="select-btn community"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMemberTypeSelect('community');
                    fetchCommunityMembers();
                  }}
                >
                  Select from Community
                </button>
                <button
                  type="button"
                  className="select-btn email"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMemberTypeSelect('email');
                  }}
                >
                  Enter Email
                </button>
              </div>

              {memberSelectType === 'community' && (
                <div className="community-members-dropdown" onClick={e => e.stopPropagation()}>
                  <select
                    value=""
                    onClick={e => e.stopPropagation()}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.target.value) {
                        handleMemberSelect(
                          e.target.value,
                          activeSlot.dayIndex,
                          activeSlot.slotIndex,
                          e
                        );
                      }
                    }}
                  >
                    <option value="">Select a member</option>
                    {communityMembers.map((member) => (
                      <option key={member.id} value={member.email}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {memberSelectType === 'email' && (
                <div className="email-input" onClick={e => e.stopPropagation()}>
                  <div className="email-input-form">
                    <input
                      type="email"
                      placeholder="Enter member's email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                    />
                    <button 
                      type="button"
                      className="submit-email-btn"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!emailInput) {
                          toast.error("Please enter an email");
                          return;
                        }
                        await handleMemberSelect(
                          emailInput,
                          activeSlot.dayIndex,
                          activeSlot.slotIndex,
                          e
                        );
                      }}
                    >
                      Add Member
                    </button>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="back-btn"
                onClick={(e) => {
                  e.preventDefault();
                  setShowMemberSelect(false);
                  setActiveSlot(null);
                  setEmailInput('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Enter service title"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <ReactQuill
              ref={editorRef}
              theme="snow"
              value={formData.description}
              onChange={(value) => setFormData({...formData, description: value})}
              modules={modules}
              formats={formats}
              placeholder="Enter service description"
              className="custom-quill-service"
            />
          </div>
          

         {serviceType !== 'webinar' && <div className="form-row">
            <div className="form-group">
              <label>Amount (₹)</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: (e.target.value)})}
                min="0"
                required
              />
            </div>

            {serviceType === 'priorityDM' ? (
              <div className="form-group timeline-group">
                <label>Response Timeline</label>
                <div className="timeline-inputs">
                  <input
                    type="number"
                    value={formData.timeline.value}
                    onChange={(e) => setFormData({
                      ...formData, 
                      timeline: { ...formData.timeline, value: Number(e.target.value) }
                    })}
                    min="1"
                    required
                  />
                  <select
                    value={formData.timeline.unit}
                    onChange={(e) => setFormData({
                      ...formData,
                      timeline: { ...formData.timeline, unit: e.target.value }
                    })}
                  >
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label>Duration (minutes)</label>
                <input
                  type="number"
                  value={formData.duration}
                  onChange={(e) => setFormData({...formData, duration: Number(e.target.value)})}
                  min="15"
                  step="15"
                  required
                />
              </div>
            )}
          </div>}

        {serviceType !== 'event' && serviceType !== 'webinar' && <div className="form-group">
            <label>Topics (comma-separated)</label>
            <input
              type="text"
              value={formData.topics}
              onChange={(e) => setFormData({...formData, topics: e.target.value})}
              placeholder="Enter topics, separated by commas"
              required
            />
          </div>}

          {serviceType === 'meeting' && formData.duration > 0 && !showTimeSlots && (
            <div className="calendar-section">
              <h3>Select Available Days</h3>
              <div className="calendar-controls">
                <div className="date-list">
                  {selectedDays.length === 0 ? (
                    <span style={{ color: 'var(--d-l-grey)', padding: '5px' }}>
                      No dates selected
                    </span>
                  ) : (
                    selectedDays.map(date => (
                      <div key={date.toISOString()} className="selected-date">
                        <span>{format(date, 'MMM dd, yyyy')}</span>
                        <button
                          onClick={() => {
                            setSelectedDays(prev => 
                              prev.filter(d => d.toISOString() !== date.toISOString())
                            );
                          }}
                          title="Remove date"
                        >
                          ×
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              
              <Calendar
                onChange={handleDaySelect}
                value={null}
                minDate={new Date()}
                selectRange={false}
                tileClassName={({ date }) => {
                  const dateStr = date.toISOString().split('T')[0];
                  return selectedDays.some(d => d.toISOString().split('T')[0] === dateStr) 
                    ? 'selected-day' 
                    : '';
                }}
              />
              
              <div className="button-group">
                <button 
                  type="button"
                  className="next-btn"
                  onClick={handleTimeSlotSubmit}
                  disabled={selectedDays.length === 0}
                >
                  Next: Set Time Slots
                </button>
              </div>
            </div>
          )}

          {serviceType === 'meeting' && showTimeSlots && (
            <div className="availability-section">
              <h3>Set Time Slots</h3>
              <div className="days-grid">
                {formData.availability.map((day, dayIndex) => (
                  <div key={day.day} className="day-slots">
                    <div className="day-header">
                      <h4>{new Date(day.day).toLocaleDateString()}</h4>
                    </div>
                    {day.slots.map((slot, slotIndex) => (
                      <div key={slotIndex} className="time-slot">
                        <div className="time-inputs">
                          <input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) => handleSlotChange(dayIndex, slotIndex, 'startTime', e.target.value)}
                          />
                          <span>to</span>
                          <input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) => handleSlotChange(dayIndex, slotIndex, 'endTime', e.target.value)}
                          />
                        </div>
                        <div className="slot-member">
                          {slot.memberEmail ? (
                            <div className="selected-member">
                              <span>{slot.memberEmail}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSlotMemberSelection(dayIndex, slotIndex);
                                }}
                                className="change-member-btn"
                              >
                                Change
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSlotMemberSelection(dayIndex, slotIndex);
                              }}
                              className="select-member-btn"
                            >
                              Select Member
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              
              <button 
                type="button"
                className="back-btn"
                onClick={() => setShowTimeSlots(false)}
              >
                Back to Calendar
              </button>
            </div>
          )}

          {serviceType === 'event' && (
            <div className="form-row">
              <div className="form-group">
                <label>Discount (%)</label>
                <input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: e.target.value === '' ? '' : Number(e.target.value)})}
                  min="0"
                  max="100"
                />
              </div>
            </div>
          )}

          {serviceType === 'webinar' && (
            <>
              <div className="service-image-upload">
                <div 
                  className="service-image-upload__container"
                  onClick={() => document.getElementById('service-image').click()}
                >
                  {previewImage ? (
                    <div className="service-image-upload__preview">
                      <img src={previewImage} alt="Webinar preview" />
                    </div>
                  ) : (
                    <div className="service-image-upload__placeholder">
                      <div className="icon-container">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                      </div>
                      <span className="upload-text">Upload Image</span>
                      <span className="upload-subtext">
                        Click or drag and drop your image here
                      </span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  id="service-image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="service-image-upload__input"
                />
              </div>

              <div className="form-group-time">
                <div className="form-group">
                  <label>Date</label>
                  <DatePicker
                    selected={formData.date}
                    onChange={handleDateChange}
                    dateFormat="MMMM d, yyyy"
                    minDate={new Date()}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Duration (min.)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: Number(e.target.value)})}
                    min="1"
                    required
                  />
                </div>
              </div>

              <div className="form-group-time">
                <div className="form-group">
                  <label>Start Time</label>
                  <DatePicker
                    selected={formData.startTime}
                    onChange={handleTimeChange}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <DatePicker
                    selected={formData.endTime}
                    readOnly
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="readonly-picker"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: (e.target.value)})}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Discount (%)</label>
                <input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: (e.target.value)})}
                  min="0"
                  max="100"
                />
              </div>
            </>
          )}

          <div className="button-group">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loader-text">
                  <span className="loader"></span>
                  {editingService ? "Updating..." : "Creating..."}
                </span>
              ) : (
                editingService ? "Update Service" : "Create Service"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddServicePopup; 